/**
 * VoiceMedia for the browser: getUserMedia, the Permissions API, device changes, the iOS
 * audio session, RTCPeerConnections and the voice playback (voiceOutput.ts), all on the
 * AudioContext shared with the game audio.
 *
 * iOS audio session: while the microphone is open, `navigator.audioSession.type` is set
 * to "play-and-record" (Safari 16.4+; elsewhere the API is missing and nothing happens).
 * Safari switches to that category by itself while capturing, but setting it explicitly
 * keeps it fixed for the whole voice session instead of following the capture state, so
 * the audio route does not flap (and Bluetooth headsets do not switch profiles) around
 * mute and device changes. WebKit's play-and-record category routes output to the
 * loudspeaker by default (not the earpiece) and to headphones when they are connected.
 * When voice is off again the type goes back to "auto": game audio alone then follows
 * Safari's default (it respects the ring/silent switch, like native games do).
 */
import type { IceServer } from '@cube-rush/shared';
import { type AudioHost, audioHost } from '../audio/context';
import { rmsDb } from '../audio/volume';
import { BrowserPeerPort } from './browserPeer';
import type {
  MicCapture,
  MicFailure,
  MicPermissionState,
  MicResult,
  PeerPort,
  PeerPortEvents,
  VoiceMedia,
} from './media';
import { VoiceOutput } from './voiceOutput';

/** Echo cancellation, noise suppression and gain control: the browser's voice processing. */
export const MIC_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

type AudioSessionType =
  | 'auto'
  | 'playback'
  | 'transient'
  | 'transient-solo'
  | 'ambient'
  | 'play-and-record';
type NavigatorWithAudioSession = Navigator & { audioSession?: { type: AudioSessionType } };

const FFT_SIZE = 1024;

export function createBrowserVoiceMedia(host: AudioHost = audioHost): VoiceMedia {
  const nav = typeof navigator === 'undefined' ? null : navigator;
  const devices = nav?.mediaDevices;
  const micSupported = typeof devices?.getUserMedia === 'function';
  const rtcSupported = typeof RTCPeerConnection === 'function';
  const output = new VoiceOutput(host);
  const mics = new WeakMap<MicCapture, MediaStreamTrack>();
  let openMics = 0;

  const setAudioSession = (type: AudioSessionType) => {
    const session = (nav as NavigatorWithAudioSession | null)?.audioSession;
    if (!session) return;
    try {
      session.type = type;
    } catch {
      // Read-only or unknown type in this version: keep the default.
    }
  };
  const micClosed = () => {
    openMics = Math.max(0, openMics - 1);
    if (openMics === 0) setAudioSession('auto');
  };

  return {
    micSupported,
    rtcSupported,

    requestMic(): Promise<MicResult> {
      if (!devices || !micSupported) return Promise.resolve({ ok: false, reason: 'unsupported' });
      // Before getUserMedia, so the session category changes once, together with capture.
      openMics += 1;
      setAudioSession('play-and-record');
      let request: Promise<MediaStream>;
      try {
        // Synchronously, still inside the user's tap.
        request = devices.getUserMedia({ audio: MIC_CONSTRAINTS, video: false });
      } catch (error) {
        micClosed();
        return Promise.resolve({ ok: false, reason: micFailure(error) });
      }
      return request.then(
        (stream): MicResult => {
          const track = stream.getAudioTracks()[0];
          if (!track) {
            for (const other of stream.getTracks()) other.stop();
            micClosed();
            return { ok: false, reason: 'not-found' };
          }
          const mic = new BrowserMic(stream, track, host, micClosed);
          mics.set(mic, track);
          return { ok: true, mic };
        },
        (error: unknown): MicResult => {
          micClosed();
          return { ok: false, reason: micFailure(error) };
        },
      );
    },

    watchMicPermission(listener: (state: MicPermissionState) => void): () => void {
      const permissions = nav?.permissions;
      if (!permissions?.query) return () => {};
      let status: PermissionStatus | null = null;
      let stopped = false;
      permissions.query({ name: 'microphone' }).then(
        (result) => {
          if (stopped) return;
          status = result;
          listener(permissionOf(result.state));
          result.onchange = () => listener(permissionOf(result.state));
        },
        () => {
          // The browser cannot tell (e.g. Firefox): stays "unknown".
        },
      );
      return () => {
        stopped = true;
        if (status) status.onchange = null;
      };
    },

    unlockPlayback(): void {
      host.unlock();
    },

    setPlaybackEnabled(enabled: boolean): void {
      output.setEnabled(enabled);
    },

    setMasterVolume(volume: number): void {
      output.setVolume(volume);
    },

    setRemoteVoiceActive(active: boolean): void {
      host.setRemoteVoiceActive(active);
    },

    createPeer(iceServers: readonly IceServer[], events: PeerPortEvents): PeerPort {
      return new BrowserPeerPort(iceServers, events, output, (mic) =>
        mic ? (mics.get(mic) ?? null) : null,
      );
    },

    onDeviceChange(listener: () => void): () => void {
      if (!devices || typeof devices.addEventListener !== 'function') return () => {};
      devices.addEventListener('devicechange', listener);
      return () => devices.removeEventListener('devicechange', listener);
    },

    isHidden(): boolean {
      return typeof document !== 'undefined' && document.visibilityState === 'hidden';
    },

    onVisible(listener: () => void): () => void {
      if (typeof document === 'undefined') return () => {};
      const handler = () => {
        if (document.visibilityState === 'visible') listener();
      };
      document.addEventListener('visibilitychange', handler);
      return () => document.removeEventListener('visibilitychange', handler);
    },

    dispose(): void {
      output.dispose();
      host.setRemoteVoiceActive(false);
    },
  };
}

/** A microphone track plus a level meter on the shared AudioContext (never played back). */
class BrowserMic implements MicCapture {
  private ctx: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private analyser: AnalyserNode | null = null;
  private readonly samples = new Float32Array(FFT_SIZE);
  private stopped = false;

  constructor(
    private readonly stream: MediaStream,
    private readonly track: MediaStreamTrack,
    private readonly host: AudioHost,
    private readonly onStop: () => void,
  ) {}

  get live(): boolean {
    return !this.stopped && this.track.readyState === 'live';
  }

  setEnabled(enabled: boolean): void {
    this.track.enabled = enabled;
  }

  level(): number {
    if (!this.live || !this.track.enabled) return Number.NEGATIVE_INFINITY;
    const analyser = this.meter();
    if (!analyser) return Number.NEGATIVE_INFINITY;
    analyser.getFloatTimeDomainData(this.samples);
    return rmsDb(this.samples);
  }

  onEnded(listener: () => void): () => void {
    const handler = () => listener();
    this.track.addEventListener('ended', handler);
    return () => this.track.removeEventListener('ended', handler);
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    for (const track of this.stream.getTracks()) track.stop();
    this.source?.disconnect();
    this.analyser?.disconnect();
    this.source = null;
    this.analyser = null;
    this.onStop();
  }

  private meter(): AnalyserNode | null {
    const ctx = this.host.context;
    if (!ctx) return null;
    if (ctx !== this.ctx) {
      this.source?.disconnect();
      try {
        this.source = ctx.createMediaStreamSource(this.stream);
      } catch {
        return null;
      }
      this.analyser = ctx.createAnalyser();
      this.analyser.fftSize = FFT_SIZE;
      this.source.connect(this.analyser);
      this.ctx = ctx;
    }
    return this.analyser;
  }
}

function micFailure(error: unknown): MicFailure {
  const name =
    typeof error === 'object' && error !== null && 'name' in error ? String(error.name) : '';
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'not-found';
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'busy';
    case 'TypeError':
      return 'unsupported';
    default:
      return 'error';
  }
}

function permissionOf(state: PermissionState): MicPermissionState {
  return state === 'granted' || state === 'denied' || state === 'prompt' ? state : 'unknown';
}
